import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

// Use the direct URL and key values for consistency
// FIXED: Signup process to resolve 401 error when creating organizations
export const SUPABASE_URL = "https://muoihzfomfercsujyyta.supabase.co";
export const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im11b2loemZvbWZlcmNzdWp5eXRhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM4Nzc5ODYsImV4cCI6MjA1OTQ1Mzk4Nn0.yOlwlmjSRiqWhjEPWgCg_jvqLEgvfzQacDhezJlQZ98";

// Initialize the Supabase client
export const supabase = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      storage: localStorage,
      detectSessionInUrl: true,
      flowType: 'pkce'
    }
  }
);

// Define available table names to ensure type safety
// Include the additional tables that are used in the application
export type TableName = 
  | keyof Database['public']['Tables'] 
  | 'stripe_connect_requests' 
  | 'assistant_feedback'
  | 'saved_reports'
  | 'report_logs';

// Custom non-Database defined tables interface
export interface AssistantFeedback {
  id: string;
  message_content: string;
  helpful: boolean;
  user_id?: string;
  user_role?: string;
  created_at: string;
}

// --------- Add saved_reports type helpers ----------
/// IMPORTANT: This matches the SQL migration, not the generated types (yet)
export interface SavedReport {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  prompt: string;
  category: string;
  conversation_history: any;
  created_at: string;
  created_by?: string | null;
}

// Type for Supabase tables
type TableTypes = Database['public']['Tables'];

// Type-safe helper function to access tables
export const tables = {
  // Generic 'from' method with proper type casting for custom tables
  from: <T extends TableName>(table: T) => {
    return supabase.from(table as any); // Use type assertion to avoid type errors
  },

  // Typed methods for standard tables
  organizations: () => supabase.from('organizations'),
  families: () => supabase.from('families'),
  parents: () => supabase.from('parents'),
  students: () => supabase.from('students'),
  classes: () => supabase.from('classes'),
  class_schedules: () => supabase.from('class_schedules'),
  class_attendance: () => supabase.from('class_attendance'),
  enrollments: () => supabase.from('enrollments'),
  invoices: () => supabase.from('invoices'),
  payments: () => supabase.from('payments'),
  locations: () => supabase.from('locations'),
  seasons: () => supabase.from('seasons'),
  staff_members: () => supabase.from('staff_members'),
  conversations: () => supabase.from('conversations'),
  messages: () => supabase.from('messages'),
  message_reads: () => supabase.from('message_reads'),
  user_profiles: () => supabase.from('user_profiles'),
  notifications: () => supabase.from('notifications'),
  user_notifications: () => supabase.from('user_notifications'),
  apparel_products: () => supabase.from('apparel_products'),
  apparel_orders: () => supabase.from('apparel_orders'),
  apparel_order_items: () => supabase.from('apparel_order_items'),
  smart_suggestions: () => supabase.from('smart_suggestions'),
  revenue_forecasts: () => supabase.from('revenue_forecasts'),
  analytics_metrics: () => supabase.from('analytics_metrics'),
  feature_requests: () => supabase.from('feature_requests'),
  feature_request_votes: () => supabase.from('feature_request_votes'),
  
  // Tables with type assertions
  policies: () => (supabase as any).from('policies'),
  policy_signatures: () => (supabase as any).from('policy_signatures'),
  
  // Use type assertion for custom tables not in Database type
  stripe_connect_requests: () => supabase.from('stripe_connect_requests' as any),
  assistant_feedback: () => supabase.from('assistant_feedback' as any),
  saved_reports: () => supabase.from('saved_reports' as any),
  report_logs: () => supabase.from('report_logs' as any),
};

// Auth helper functions
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

export const signOut = async () => {
  return await supabase.auth.signOut();
};

export const signInWithPassword = async (email: string, password: string) => {
  return await supabase.auth.signInWithPassword({ email, password });
};


export type UserRole = "bizadmin" | "parent" | "owner" | "superadmin" | "staff" | "user";

// Debug logging removed to prevent multiple GoTrueClient instances
// Auth state changes are already handled by AuthProvider

// Helper function to call Supabase Edge Functions
export const callSupabaseFunction = async (
  functionName: string,
  data?: any,
  method: 'GET' | 'POST' = 'POST'
) => {
  const session = await supabase.auth.getSession();
  
  const response = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
    method,
    headers: {
      'Authorization': `Bearer ${session.data.session?.access_token || SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Function call failed: ${response.status} ${errorText}`);
  }

  return response.json();
};

// Specific function helpers
export const stripeAPI = {
  getDashboardData: () => callSupabaseFunction('stripe-admin-dashboard', undefined, 'GET'),
  
  performSubscriptionAction: (actionData: {
    action: 'cancel' | 'pause' | 'reactivate' | 'change_plan' | 'portal_link' | 'retry_invoice';
    subscription_id?: string;
    customer_id?: string;
    new_plan_id?: string;
    proration_behavior?: 'create_prorations' | 'none' | 'always_invoice';
    cancel_at_period_end?: boolean;
  }) => callSupabaseFunction('stripe-subscription-actions', actionData),
  
  syncPricing: () => callSupabaseFunction('sync-stripe-pricing', undefined, 'GET'),
};
